import React, { useState } from "react";
import { X } from "lucide-react";
import { IconButton, SegmentedControl } from "../ui";
import { usePlan } from "../../contexts/PlanContext";
import { KIND_META } from "./resourceKinds";
import ContentUploadForm from "./ContentUploadForm";
import AssignmentForm from "./AssignmentForm";
import QuizForm from "./QuizForm";
import PickExisting from "./PickExisting";

/**
 * ResourcePicker — create a resource, or pick an existing one, to attach.
 *
 * Documents are uploaded, assignments and quizzes written on the spot; coding
 * challenges are picked from the bank. Every path ends in `onPick({ _id,
 * title, kind, contentType })`, then `onClose()`.
 *
 * Used by the syllabus builder (a topic's resources, a session's resources) and
 * by the trainer's Smart Sessions (adding during delivery).
 *
 * Props:
 *   kind        — 'document' | 'assignment' | 'quiz' | 'codingChallenge'
 *   courseId, batchId
 *   initialTab  — 'create' | 'pick' (a replace opens on 'pick')
 *   moduleTitle, sessionTitle, sessionDescription — context for AI quiz drafts
 *   allowScorm  — offer SCORM uploads (admin only)
 *   showHeader  — false when the host (a dialog) already titles it
 *   onPick, onClose
 */
const ResourcePicker = ({
  kind, courseId, batchId, initialTab, onPick, onClose,
  moduleTitle, sessionTitle, sessionDescription, allowScorm = true, showHeader = true,
}) => {
  const { hasPlanFeature } = usePlan();
  const meta = KIND_META[kind] || KIND_META.document;
  const Icon = meta.Icon;
  const quizAllowed = kind !== "quiz" || !hasPlanFeature || hasPlanFeature("quizAssessment");
  const canCreate = !!meta.create && quizAllowed;
  const [tab, setTab] = useState(() => (canCreate && initialTab !== "pick" ? "create" : "pick"));
  const creating = tab === "create" && canCreate;

  const picked = (resource) => {
    if (resource?._id) onPick(resource);
    onClose();
  };

  return (
    <section aria-label={`${creating ? "New" : "Pick"} ${meta.label.toLowerCase()}`} className="tw-flex tw-min-h-0 tw-flex-col tw-gap-4">
      {showHeader && (
        <header className="tw-flex tw-items-center tw-gap-2.5">
          <span className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary">
            <Icon size={16} aria-hidden="true" />
          </span>
          <h3 className="tw-m-0 tw-flex-1 tw-text-[0.9375rem] tw-font-semibold tw-text-content">
            {creating ? `New ${meta.label.toLowerCase()}` : `Pick ${meta.label === "Quiz" ? "a quiz" : `a ${meta.label.toLowerCase()}`}`}
          </h3>
          <IconButton size="sm" icon={X} label="Close" onClick={onClose} />
        </header>
      )}

      {canCreate && (
        <SegmentedControl
          label={`Add a ${meta.label.toLowerCase()}`}
          value={tab}
          onChange={setTab}
          options={[
            { value: "create", label: meta.createLabel },
            { value: "pick", label: "Pick existing" },
          ]}
        />
      )}

      {creating && meta.create === "upload" && (
        <ContentUploadForm courseId={courseId} allowScorm={allowScorm} onCreated={picked} onCancel={onClose} />
      )}
      {creating && meta.create === "assignment" && (
        <AssignmentForm courseId={courseId} batchId={batchId} onCreated={picked} onCancel={onClose} />
      )}
      {creating && meta.create === "quiz" && (
        <QuizForm
          courseId={courseId}
          context={{ moduleTitle, sessionTitle, sessionDescription }}
          onCreated={picked}
          onCancel={onClose}
        />
      )}
      {!creating && (
        <PickExisting kind={kind} meta={meta} courseId={courseId} batchId={batchId} onPick={picked} />
      )}
    </section>
  );
};

export default ResourcePicker;
